import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";

/**
 * Real-time WebSocket context.
 *
 * Single shared connection per browser tab. Anything in the app that wants
 * live updates calls `useWebSocket().subscribe(event, handler)` and gets a
 * cleanup function back — no polling, no global event-bus hacks.
 *
 * Resilience strategy:
 *   - Auto-reconnect with exponential backoff (max 30s) so a flaky network
 *     doesn't permanently break the live experience.
 *   - Heartbeat ping every 25s — if the socket is half-dead we'll notice
 *     within ~50s and reconnect.
 *   - Pause when document.hidden, resume on visibilitychange so we don't
 *     burn battery while the tab is backgrounded.
 *   - Token is passed via ?token= query param because browsers can't set
 *     Authorization headers on WS handshakes.
 */
const WebSocketContext = createContext({
  connected: false,
  subscribe: () => () => {},
});

const HEARTBEAT_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

function getToken() {
  try { return localStorage.getItem("gwg_auth_token") || ""; } catch { return ""; }
}

function buildWsUrl() {
  const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  // Convert https://x → wss://x  AND  http://x → ws://x
  const wsBase = backend.replace(/^http/, "ws");
  const token = getToken();
  return `${wsBase}/api/ws${token ? `?token=${encodeURIComponent(token)}` : ""}`;
}

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const attemptsRef = useRef(0);
  const subscribersRef = useRef(new Map()); // event -> Set<handler>
  const closedByUsRef = useRef(false);

  const dispatch = useCallback((evt, payload) => {
    const handlers = subscribersRef.current.get(evt);
    if (!handlers) return;
    handlers.forEach((h) => { try { h(payload); } catch (e) { console.warn("ws handler error", e); } });
  }, []);

  const cleanupSocket = useCallback(() => {
    if (heartbeatTimerRef.current) { clearInterval(heartbeatTimerRef.current); heartbeatTimerRef.current = null; }
    if (wsRef.current) {
      try { wsRef.current.onopen = wsRef.current.onmessage = wsRef.current.onerror = wsRef.current.onclose = null; } catch {}
      try { wsRef.current.close(); } catch {}
      wsRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!user) return;
    if (typeof WebSocket === "undefined") return;
    if (wsRef.current && (wsRef.current.readyState === 0 || wsRef.current.readyState === 1)) return;
    cleanupSocket();
    closedByUsRef.current = false;

    let ws;
    try { ws = new WebSocket(buildWsUrl()); }
    catch (e) { scheduleReconnect(); return; }
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      attemptsRef.current = 0;
      // Heartbeat — server replies with "pong"; we don't care about the
      // reply, the loss of it will eventually trigger onclose.
      heartbeatTimerRef.current = setInterval(() => {
        if (ws.readyState === 1) { try { ws.send("ping"); } catch {} }
      }, HEARTBEAT_MS);
    };

    ws.onmessage = (e) => {
      if (e.data === "pong") return;
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (msg && msg.event) dispatch(msg.event, msg.payload || {});
    };

    ws.onerror = () => {
      // The accompanying onclose will handle reconnection.
    };

    ws.onclose = () => {
      setConnected(false);
      if (!closedByUsRef.current) scheduleReconnect();
    };
  }, [user, cleanupSocket, dispatch]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return;
    const attempt = attemptsRef.current + 1;
    attemptsRef.current = attempt;
    const delay = Math.min(MAX_BACKOFF_MS, 500 * 2 ** Math.min(attempt, 6));
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connect();
    }, delay);
  }, [connect]);

  // Lifecycle: connect when we have a user, tear down on logout.
  useEffect(() => {
    if (!user) {
      closedByUsRef.current = true;
      cleanupSocket();
      setConnected(false);
      return;
    }
    connect();

    const onVis = () => {
      if (document.visibilityState === "visible") {
        // Force a fresh check; if socket is dead this will reconnect.
        if (!wsRef.current || wsRef.current.readyState > 1) connect();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);

    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
      closedByUsRef.current = true;
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      cleanupSocket();
    };
  }, [user, connect, cleanupSocket]);

  const subscribe = useCallback((event, handler) => {
    if (!subscribersRef.current.has(event)) subscribersRef.current.set(event, new Set());
    subscribersRef.current.get(event).add(handler);
    return () => {
      const set = subscribersRef.current.get(event);
      if (set) { set.delete(handler); if (set.size === 0) subscribersRef.current.delete(event); }
    };
  }, []);

  return (
    <WebSocketContext.Provider value={{ connected, subscribe }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export const useWebSocket = () => useContext(WebSocketContext);
